import React, { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { DefaultButton, PrimaryButton } from "@fluentui/react";
import useHistorySummary from "./useHistorySummary";
import ProgressSteps, { SUMMARY_STEPS, stepListFor } from "./ProgressSteps";
import "./AiDraft.css";

// Same allowlist the AI service and the backend apply; sanitised a third time before it is shown.
const SUMMARY_TAGS = ["h4", "p", "ul", "ol", "li", "strong", "em", "br"];

export const sanitizeSummaryHtml = (html) => DOMPurify.sanitize(html || "", { ALLOWED_TAGS: SUMMARY_TAGS, ALLOWED_ATTR: [] });

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "2 notes and 3 documents", "1 note", "3 documents". */
export const describeSources = (noteCount = 0, documentCount = 0) =>
  [noteCount > 0 && plural(noteCount, "note"), documentCount > 0 && plural(documentCount, "document")]
    .filter(Boolean)
    .join(" and ");

const formatTime = (iso) => {
  const date = iso ? new Date(iso) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleString() : "";
};

const elapsed = (iso, now) => {
  const started = Date.parse(iso || "");
  return Number.isNaN(started) ? null : Math.max(0, Math.floor((now - started) / 1000));
};

/** Every summary step with its state (see ProgressSteps.stepListFor). */
export const stepList = (recorded = []) => stepListFor(SUMMARY_STEPS, recorded);

const OUTLINE_BUTTON = {
  root: { background: "transparent", color: "inherit", borderColor: "rgba(127, 127, 127, 0.6)", flexShrink: 0 },
  rootHovered: { background: "rgba(127, 127, 127, 0.18)", color: "inherit" },
  rootPressed: { background: "rgba(127, 127, 127, 0.28)", color: "inherit" },
  rootDisabled: { background: "transparent", color: "inherit", opacity: 0.5 },
  label: { fontWeight: 600 },
};

const Steps = ({ steps, now }) => (
  <ProgressSteps definitions={SUMMARY_STEPS} steps={steps} now={now} testId="ai-history-summary-steps" />
);

/**
 * AI summary of the study's clinical notes and history documents, for the radiologist: generate on
 * demand, read it here, regenerate when the notes or documents change. It is decision support only;
 * nothing is copied into the report. Renders nothing when the feature is off for the study.
 *
 * @param studyId DB id of the open study
 * @param compact smaller layout for the toolbar's history / clinical-notes popover
 * @param active  false: fetch nothing (e.g. not a radiologist)
 * @param color   text colour (the popover sets it per theme)
 */
const HistorySummary = ({ studyId, compact = false, active = true, color }) => {
  const { data, busy, error, generate } = useHistorySummary(studyId, active);
  const [now, setNow] = useState(() => Date.now());
  const [showSources, setShowSources] = useState(false);
  const summary = data?.summary || null;
  const running = !!summary?.running;
  const html = useMemo(() => sanitizeSummaryHtml(summary?.summary_html), [summary?.summary_html]);

  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [running]);

  if (!data?.enabled) return null;

  const counts = describeSources(data.noteCount, data.documentCount);
  const seconds = running ? elapsed(summary.requested_at, now) : null;
  const done = summary?.status === "completed" && !!html;
  const failed = summary?.status === "failed";
  const sources = Array.isArray(summary?.sources) ? summary.sources : [];

  return (
    <section
      className={`ai-history-summary${compact ? " ai-history-summary--compact" : ""}`}
      data-testid="ai-history-summary"
      aria-label="AI history summary"
      style={color ? { color } : undefined}
      // the toolbar popover closes on outside clicks; clicks in here must not count as outside
      onClick={(e) => e.stopPropagation()}
    >
      <header className="ai-history-summary-header">
        <span className="ai-history-summary-title">History summary (AI)</span>
        {data.hasHistory && !running && (
          <DefaultButton
            className="ai-history-summary-button"
            text={summary ? "Regenerate" : "Summarise history"}
            onClick={generate}
            disabled={busy}
            // follows the popover's theme instead of Fluent's white button
            styles={OUTLINE_BUTTON}
          />
        )}
      </header>

      {!data.hasHistory && (
        <div className="ai-draft-panel-meta">This study has no clinical notes or history documents to summarise.</div>
      )}
      {data.hasHistory && !summary && !running && (
        <div className="ai-draft-panel-meta">Summarise {counts} with AI before reading them one by one.</div>
      )}
      {error && (
        <div className="ai-draft-panel-callout ai-draft-panel-callout--error" role="alert">
          {error}
        </div>
      )}
      {running && (
        <div className="ai-draft-pending" role="status" aria-live="polite" data-testid="ai-history-summary-pending">
          <div className="ai-draft-pending-text">
            <span>
              Summarising {counts}…{seconds != null && <span> {seconds} s</span>}
            </span>
          </div>
          <Steps steps={summary.steps} now={now} />
        </div>
      )}
      {failed && (
        <div className="ai-draft-panel-callout ai-draft-panel-callout--error" role="alert">
          {summary.error || "The history summary could not be generated."}
          {summary.steps?.length > 0 && <Steps steps={summary.steps} now={now} />}
          {data.hasHistory && <PrimaryButton className="ai-history-summary-retry" text="Try again" onClick={generate} disabled={busy} />}
        </div>
      )}
      {done && summary.stale && !running && (
        <div className="ai-draft-panel-callout ai-draft-panel-callout--warning" role="status">
          Clinical notes or history documents changed since this summary. Regenerate to include them.
        </div>
      )}
      {done && (
        <>
          <div
            className="ai-history-summary-body"
            data-testid="ai-history-summary-html"
            // sanitised with the summary allowlist above
            dangerouslySetInnerHTML={{ __html: html }}
          />
          <div className="ai-draft-panel-meta ai-history-summary-meta">
            AI-generated from the notes and documents: check the originals before relying on it.
            {[
              summary.note_count != null &&
                summary.document_count != null &&
                ` Based on ${describeSources(summary.note_count, summary.document_count) || "no sources"}`,
              summary.model_name && ` · ${summary.model_name}`,
              summary.completed_at && ` · ${formatTime(summary.completed_at)}`,
            ]
              .filter(Boolean)
              .join("")}
          </div>
          {(sources.length > 0 || summary.notes) && (
            <button type="button" className="ai-history-summary-link" onClick={() => setShowSources((s) => !s)}>
              {showSources ? "Hide documents read" : "Documents read"}
            </button>
          )}
          {showSources && (
            <ul className="ai-history-summary-sources" data-testid="ai-history-summary-sources">
              {sources.map((s, i) => (
                <li key={`${s.name}-${i}`}>
                  {s.used ? "✓" : "✗"} {s.name}
                  {s.note ? ` (${s.note})` : ""}
                </li>
              ))}
              {summary.notes &&
                summary.notes.split("\n").map((note) => (
                  <li key={note} className="ai-history-summary-note">
                    {note}
                  </li>
                ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
};

export default HistorySummary;
